/**
 * features/communications/ParentNoticesManager.tsx
 * ------------------------------------------------------------------
 * Composer for parent notices. Two modes, one component:
 *
 *  • SCHOOL mode (Part A, default — no props): admin/head broadcast to
 *    any audience (whole school / grades / classes / pupils). Recipient
 *    parents are resolved CLIENT-SIDE from the loaded `users` directory
 *    and written into recipientParentUids at publish.
 *
 *  • TEACHER mode (Part B — pass `reach`): a teaching-staff member
 *    messages the parents of their OWN pupils. Audience is restricted to
 *    the teacher's reach (their classes / their pupils); there is no
 *    school/grades option. Crucially, parents are NOT resolved here —
 *    teachers cannot read `users`. The notice is written with
 *    scope:'teacher' and an empty recipient list; the onCreate Cloud
 *    Function re-validates the author's reach server-side and stamps
 *    recipientParentUids/recipientCount. The live count shown here is a
 *    pupils'-families proxy, not the authoritative parent count.
 *
 * GATING — school mode is gated on the admin/head set that loads `users`
 * (load-bearing: client resolution needs that directory). Teacher mode
 * is reached only via TeacherParentComms, which gates on
 * canMessageParents and supplies `reach`; this component trusts that.
 * ------------------------------------------------------------------
 */
import { useEffect, useMemo, useState } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import {
  Megaphone,
  Send,
  School,
  GraduationCap,
  Users as UsersIcon,
  User as UserIcon,
  Mail,
  Search,
  AlertCircle,
  CheckCircle2,
  Paperclip,
  FileText,
  ImageIcon,
  X as XIcon,
} from 'lucide-react';
import { doc, collection as fbCollection } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, setDoc } from '@/lib/firebase';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { GradientHero, SoftCard, AccentButton, glassFieldClass } from '@/components/ui/glass';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { sanitizeText, sanitizeComment } from '@/lib/sanitize';
import NoticeReadTracking from '@/features/communications/NoticeReadTracking';
import type { TeacherReach } from '@/lib/teacherReach';
import {
  PARENT_NOTICES_COLLECTION,
  resolveRecipients,
  describeAudience,
  type NoticeAudience,
  type NoticeAudienceKind,
  type NoticePriority,
  type NoticeType,
  type ParentNotice,
  type ResolverParent,
  type NoticeAttachment,
  attachmentRejection,
  formatBytes,
  safeAttachmentName,
  NOTICE_ATTACHMENT_ACCEPT,
  NOTICE_ATTACHMENT_MAX_COUNT,
} from '@/lib/parentNotices';

/** Must match DataContext.ADMIN_OR_HEAD — the set that loads `users`. */
const ADMIN_OR_HEAD = ['admin', 'systemAdmin', 'academicAdmin', 'headteacher'];

type SaveStatus = 'saving' | 'success' | 'error';

interface AudienceTab {
  kind: NoticeAudienceKind;
  label: string;
  icon: typeof School;
}
const SCHOOL_TABS: AudienceTab[] = [
  { kind: 'school', label: 'Whole school', icon: School },
  { kind: 'grades', label: 'Forms', icon: GraduationCap },
  { kind: 'classes', label: 'Classes', icon: UsersIcon },
  { kind: 'students', label: 'Individual pupils', icon: UserIcon },
];
const TEACHER_TABS: AudienceTab[] = [
  { kind: 'classes', label: 'My classes', icon: UsersIcon },
  { kind: 'students', label: 'Individual pupils', icon: UserIcon },
];

export interface ParentNoticesManagerProps {
  /** Present => teacher-scoped mode, restricting audience to this reach. */
  reach?: TeacherReach;
  /** Stamped as createdByRole (teacher mode). */
  senderRole?: string;
  /** Stamped as department (teacher mode). */
  senderDepartment?: string | null;
}

export default function ParentNoticesManager({
  reach,
  senderRole,
  senderDepartment,
}: ParentNoticesManagerProps = {}) {
  const { userData, userRoles } = useAuth();
  const { students, classes, users } = useData();

  const teacherMode = !!reach;
  const adminOrHead = userRoles.some((r) => ADMIN_OR_HEAD.includes(r));
  const tabs = teacherMode ? TEACHER_TABS : SCHOOL_TABS;

  /* ---- compose state ---- */
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<NoticePriority>('normal');
  const [kind, setKind] = useState<NoticeAudienceKind>(teacherMode ? 'classes' : 'school');
  const [forms, setForms] = useState<string[]>([]);
  const [classIds, setClassIds] = useState<string[]>([]);
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [studentSearch, setStudentSearch] = useState('');
  /* Always true now — kept on the document because notices raised before
     this change carry it, and the email trigger still reads it. */
  const emailOnPublish = true;
  /* Attachments are held as Files and uploaded only at publish. Uploading on
     pick would leave orphans in Storage every time somebody starts a notice
     and abandons it, and nothing would ever clean them up. */
  const [files, setFiles] = useState<File[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const [noticeType, setNoticeType] = useState<NoticeType>('general');
  const [saving, setSaving] = useState(false);
  const [save, setSave] = useState<{ show: boolean; status: SaveStatus; message: string }>({
    show: false,
    status: 'saving',
    message: '',
  });

  /* ---- recent sent notices ----
     school mode: all notices (admin reads all) ordered by createdAt.
     teacher mode: only my own (where createdBy == uid), no orderBy to
     avoid a composite index — sorted client-side. */
  const [recent, setRecent] = useState<ParentNotice[]>([]);
  const [tracking, setTracking] = useState<ParentNotice | null>(null);
  const uid = userData?.uid;
  useEffect(() => {
    if (!teacherMode && !adminOrHead) return;
    if (teacherMode && !uid) return;
    const q = teacherMode
      ? query(collection(db, PARENT_NOTICES_COLLECTION), where('createdBy', '==', uid))
      : query(collection(db, PARENT_NOTICES_COLLECTION), orderBy('createdAt', 'desc'), limit(25));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map(
          (d) => ({ id: d.id, ...(d.data() as object) }) as ParentNotice,
        );
        if (teacherMode) {
          list.sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
        }
        setRecent(teacherMode ? list.slice(0, 25) : list);
      },
      (err) => console.warn('[parent-notices] recent subscription error:', err),
    );
    return unsub;
  }, [teacherMode, adminOrHead, uid]);

  /* ---- resolver inputs (school mode resolves parents client-side) ---- */
  const parents: ResolverParent[] = useMemo(
    () =>
      teacherMode
        ? []
        : users
            .filter((u) => {
              const rs = u.roles && u.roles.length ? u.roles : u.role ? [u.role] : [];
              return rs.includes('parent');
            })
            .map((u) => ({
              uid: u.uid ?? u.id, // gotcha #1
              studentIds: u.parentAccount?.studentIds ?? [],
              parentName: u.displayName,
            })),
    [teacherMode, users],
  );
  const resolverStudents = useMemo(
    () => students.map((s) => ({ id: s.id, currentClass: s.currentClass })),
    [students],
  );
  const resolverClasses = useMemo(
    () => classes.map((c) => ({ id: c.id, form: c.form })),
    [classes],
  );
  const classesForLabel = useMemo(
    () => classes.map((c) => ({ id: c.id, name: c.name, form: c.form })),
    [classes],
  );

  /* ---- audience source lists (teacher mode is restricted to reach) ---- */
  const availableClasses = useMemo(() => {
    const base =
      teacherMode && reach ? classes.filter((c) => reach.classIds.includes(c.id)) : classes;
    return base
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
  }, [teacherMode, reach, classes]);

  const availableStudents = useMemo(() => {
    if (teacherMode && reach) {
      const set = new Set(reach.studentIds);
      return students.filter((s) => set.has(s.id));
    }
    return students;
  }, [teacherMode, reach, students]);

  const allForms = useMemo(() => {
    const set = new Set<string>();
    classes.forEach((c) => c.form && set.add(c.form));
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [classes]);

  /* ---- current audience ---- */
  const audience: NoticeAudience = useMemo(() => {
    switch (kind) {
      case 'grades':
        return { kind, forms };
      case 'classes':
        return { kind, classIds };
      case 'students':
        return { kind, studentIds };
      default:
        return { kind: 'school' };
    }
  }, [kind, forms, classIds, studentIds]);

  /* ---- school mode: client-side parent resolution ---- */
  const resolved = useMemo(
    () =>
      teacherMode
        ? { parentUids: [] as string[], studentIds: [] as string[], parents: [] as ResolverParent[] }
        : resolveRecipients(audience, {
            parents,
            students: resolverStudents,
            classes: resolverClasses,
          }),
    [teacherMode, audience, parents, resolverStudents, resolverClasses],
  );

  /* ---- teacher mode: pupils'-families proxy count ---- */
  const targetStudentIds = useMemo(() => {
    if (!teacherMode || !reach) return [];
    if (kind === 'students') return studentIds.filter((id) => reach.studentIds.includes(id));
    if (kind === 'classes') {
      const out = new Set<string>();
      classIds.forEach((cid) => (reach.byClass[cid] ?? []).forEach((sid) => out.add(sid)));
      return [...out];
    }
    return [];
  }, [teacherMode, reach, kind, studentIds, classIds]);

  const displayCount = teacherMode ? targetStudentIds.length : resolved.parentUids.length;

  const audienceLabel = useMemo(
    () => describeAudience(audience, { classes: classesForLabel, studentCount: studentIds.length }),
    [audience, classesForLabel, studentIds.length],
  );

  /* ---- student picker filtering ---- */
  const filteredStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    const base = q
      ? availableStudents.filter((s) => s.fullName.toLowerCase().includes(q))
      : availableStudents;
    return base.slice(0, 60);
  }, [availableStudents, studentSearch]);

  /* ---- validity ---- */
  const audienceChosen =
    kind === 'school' ||
    (kind === 'grades' && forms.length > 0) ||
    (kind === 'classes' && classIds.length > 0) ||
    (kind === 'students' && studentIds.length > 0);
  const directoryReady = teacherMode || users.length > 0;
  const canPublish =
    (teacherMode || adminOrHead) &&
    directoryReady &&
    !saving &&
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    audienceChosen &&
    displayCount > 0;

  function toggle(list: string[], value: string): string[] {
    return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
  }

  function resetForm() {
    setTitle('');
    setBody('');
    setPriority('normal');
    setKind(teacherMode ? 'classes' : 'school');
    setForms([]);
    setClassIds([]);
    setStudentIds([]);
    setStudentSearch('');
    setNoticeType('general');
    setFiles([]);
    setAttachError(null);
  }

  function addFiles(picked: FileList | null) {
    if (!picked || !picked.length) return;
    const incoming = Array.from(picked);
    const errors: string[] = [];
    const accepted: File[] = [];
    for (const f of incoming) {
      const why = attachmentRejection(f);
      if (why) { errors.push(why); continue; }
      // Same name twice is nearly always the same file picked twice.
      if (files.some((e) => e.name === f.name && e.size === f.size)) continue;
      accepted.push(f);
    }
    const room = NOTICE_ATTACHMENT_MAX_COUNT - files.length;
    if (accepted.length > room) {
      errors.push(`Only ${NOTICE_ATTACHMENT_MAX_COUNT} files can go on one notice.`);
    }
    setFiles([...files, ...accepted.slice(0, Math.max(0, room))]);
    setAttachError(errors.length ? errors.join(' ') : null);
  }

  const removeFile = (i: number) => setFiles(files.filter((_, n) => n !== i));

  /**
   * Upload the picked files under the notice's id.
   *
   * The id is minted BEFORE the document is written so the storage path and
   * the notice cannot disagree, and so a failed upload aborts the publish
   * rather than producing a notice that promises a letter it does not have.
   */
  async function uploadAttachments(noticeId: string): Promise<NoticeAttachment[]> {
    if (!files.length) return [];
    const out: NoticeAttachment[] = [];
    setUploading({ done: 0, total: files.length });
    const uploaded: string[] = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const name = safeAttachmentName(f.name);
        const path = `parentNotices/${noticeId}/${Date.now()}_${i}_${name}`;
        const r = storageRef(storage, path);
        await uploadBytes(r, f, {
          contentType: f.type,
          // So a parent's browser saves it under the name the school chose
          // rather than the timestamped path.
          contentDisposition: `attachment; filename="${name}"`,
        });
        uploaded.push(path);
        out.push({ name, url: await getDownloadURL(r), path, contentType: f.type, size: f.size });
        setUploading({ done: i + 1, total: files.length });
      }
      return out;
    } catch (e) {
      /* Try to leave nothing behind. Best effort by design: storage.rules
         allows deletion only to admin/head, so a teacher whose upload fails
         part-way leaves files nobody will reference. That is the deliberate
         trade — a teacher who can attach a letter to a live notice must not
         be able to make one disappear from it afterwards. */
      for (const p of uploaded) {
        try { await deleteObject(storageRef(storage, p)); } catch { /* see above */ }
      }
      throw e;
    } finally {
      setUploading(null);
    }
  }

  async function publish() {
    if (!canPublish) return;
    setSaving(true);
    setSave({ show: true, status: 'saving', message: 'Publishing notice…' });
    try {
      const now = new Date().toISOString();
      const common = {
        title: sanitizeText(title, 200),
        body: sanitizeComment(body, 5000),
        priority,
        audience,
        audienceLabel,
        emailOnPublish,
        createdBy: userData?.uid ?? '',
        createdByName: userData?.displayName ?? '',
        createdAt: now,
      };
      // Mint the id first: the attachments live under it in Storage, so the
      // path cannot be written before the document exists unless we choose
      // the id ourselves.
      const ref = doc(fbCollection(db, PARENT_NOTICES_COLLECTION));
      const attachments = await uploadAttachments(ref.id);

      const payload = teacherMode
        ? {
            ...common,
            scope: 'teacher' as const,
            noticeType,
            department: senderDepartment ?? null,
            createdByRole: senderRole ?? userData?.role,
            // Resolved server-side (teachers can't read `users`):
            recipientParentUids: [] as string[],
            recipientCount: 0,
          }
        : {
            ...common,
            scope: 'school' as const,
            department: null,
            createdByRole: userData?.role,
            recipientParentUids: resolved.parentUids,
            recipientCount: resolved.parentUids.length,
          };
      await setDoc(ref, { ...payload, ...(attachments.length ? { attachments } : {}) });
      setSave({
        show: true,
        status: 'success',
        message: teacherMode
          ? `Notice sent to the families of ${displayCount} pupil${displayCount === 1 ? '' : 's'}.`
          : `Notice published to ${displayCount} parent${displayCount === 1 ? '' : 's'}.`,
      });
      resetForm();
    } catch (e) {
      setSave({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  if (!teacherMode && !adminOrHead) {
    return (
      <SoftCard>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <AlertCircle size={16} />
          Parent notices broadcast is available to administrators and heads only.
        </div>
      </SoftCard>
    );
  }

  return (
    <div className="space-y-6">
      <GradientHero
        icon={Megaphone}
        title={teacherMode ? 'Message Parents' : 'Parent Notices'}
        subtitle={
          teacherMode
            ? 'Send a notice to the parents of your own pupils'
            : 'Broadcast a notice to the right parents — in their portal, optionally by email'
        }
      />

      <SoftCard>
        <div className="space-y-5">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Title
            </label>
            <input
              className={glassFieldClass()}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                teacherMode
                  ? 'e.g. Maths homework due Friday'
                  : 'e.g. Half-term closure & reopening dates'
              }
              maxLength={200}
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              Message
            </label>
            <textarea
              className={glassFieldClass('min-h-[140px] resize-y')}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write the notice parents will read…"
              maxLength={5000}
            />
          </div>

          {/* Priority + (teacher) notice type */}
          <div className="flex flex-wrap gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Priority
              </label>
              <div className="flex gap-2">
                {(['normal', 'high'] as NoticePriority[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPriority(p)}
                    className={`px-4 py-2 text-sm font-medium rounded-xl border-2 transition-all ${
                      priority === p
                        ? p === 'high'
                          ? 'border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          : 'border-[#2d5295] bg-blue-50 text-[#1b3a6b] dark:bg-blue-900/30 dark:text-blue-300'
                        : 'border-gray-200 text-gray-600 dark:border-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {p === 'high' ? 'High' : 'Normal'}
                  </button>
                ))}
              </div>
            </div>

            {teacherMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  Type
                </label>
                <div className="flex gap-2">
                  {(['general', 'homework'] as NoticeType[]).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setNoticeType(t)}
                      className={`px-4 py-2 text-sm font-medium rounded-xl border-2 transition-all capitalize ${
                        noticeType === t
                          ? 'border-[#2d5295] bg-blue-50 text-[#1b3a6b] dark:bg-blue-900/30 dark:text-blue-300'
                          : 'border-gray-200 text-gray-600 dark:border-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Audience */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              Audience
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {tabs.map(({ kind: k, label, icon: Icon }) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setKind(k)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl border-2 transition-all ${
                    kind === k
                      ? 'border-[#2d5295] bg-blue-50 text-[#1b3a6b] dark:bg-blue-900/30 dark:text-blue-300'
                      : 'border-gray-200 text-gray-600 dark:border-gray-600 dark:text-gray-300'
                  }`}
                >
                  <Icon size={15} />
                  {label}
                </button>
              ))}
            </div>

            {kind === 'grades' && !teacherMode && (
              <div className="flex flex-wrap gap-2">
                {allForms.length === 0 && (
                  <span className="text-sm text-gray-500">No forms found.</span>
                )}
                {allForms.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setForms((cur) => toggle(cur, f))}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${
                      forms.includes(f)
                        ? 'border-[#2d5295] bg-[#2d5295] text-white'
                        : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
                    }`}
                  >
                    Form {f}
                  </button>
                ))}
              </div>
            )}

            {kind === 'classes' && (
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                {availableClasses.length === 0 && (
                  <span className="text-sm text-gray-500">
                    {teacherMode ? 'You have no classes to message.' : 'No classes found.'}
                  </span>
                )}
                {availableClasses.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setClassIds((cur) => toggle(cur, c.id))}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-all ${
                      classIds.includes(c.id)
                        ? 'border-[#2d5295] bg-[#2d5295] text-white'
                        : 'border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300'
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}

            {kind === 'students' && (
              <div className="space-y-2">
                <div className="relative">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    className={glassFieldClass('pl-9')}
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    placeholder={teacherMode ? 'Search your pupils…' : 'Search pupils by name…'}
                  />
                </div>
                {studentIds.length > 0 && (
                  <div className="text-xs text-gray-500">{studentIds.length} selected</div>
                )}
                <div className="max-h-56 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredStudents.map((s) => {
                    const checked = studentIds.includes(s.id);
                    const cls = classes.find((c) => c.id === s.currentClass);
                    return (
                      <label
                        key={s.id}
                        className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => setStudentIds((cur) => toggle(cur, s.id))}
                        />
                        <span className="text-gray-800 dark:text-gray-100">{s.fullName}</span>
                        {cls && <span className="ml-auto text-xs text-gray-400">{cls.name}</span>}
                      </label>
                    );
                  })}
                  {filteredStudents.length === 0 && (
                    <div className="px-3 py-4 text-sm text-gray-500 text-center">
                      No pupils match that search.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                <Paperclip size={15} /> Attachments
                <span className="text-xs font-normal text-gray-400">
                  PDF, JPG, PNG or WebP · up to {NOTICE_ATTACHMENT_MAX_COUNT} files, 10 MB each
                </span>
              </span>
              <label className="cursor-pointer text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20">
                Choose files
                <input
                  type="file"
                  multiple
                  accept={NOTICE_ATTACHMENT_ACCEPT}
                  className="hidden"
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                />
              </label>
            </div>

            {attachError && (
              <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg px-3 py-2">
                {attachError}
              </div>
            )}

            {files.length > 0 && (
              <ul className="space-y-1.5">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center gap-2.5 text-sm bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
                  >
                    {f.type.startsWith('image/')
                      ? <ImageIcon size={15} className="text-primary shrink-0" />
                      : <FileText size={15} className="text-primary shrink-0" />}
                    <span className="truncate text-gray-800 dark:text-gray-100">{f.name}</span>
                    <span className="ml-auto text-xs text-gray-400 shrink-0">{formatBytes(f.size)}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-gray-400 hover:text-red-600 shrink-0"
                      title="Remove"
                    >
                      <XIcon size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {uploading && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Uploading {uploading.done} of {uploading.total}…
              </div>
            )}
          </div>

          {/* EVERY parent is emailed. This used to be an opt-in checkbox that
              defaulted to off, so a notice could be published to the portal
              and reach nobody who was not already looking at it. */}
          <div className="flex items-start gap-2.5 text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2.5">
            <Mail size={15} className="mt-0.5 shrink-0 text-primary" />
            <span>
              Every parent above is emailed automatically, telling them a message is waiting in
              the portal — with instructions for anyone who cannot sign in.
              {files.length > 0 && ' Attachments are linked from the email.'}
            </span>
          </div>

          {/* Summary + publish */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-gray-100 dark:border-gray-700">
            <div className="text-sm">
              <span className="font-semibold text-gray-900 dark:text-white">
                {teacherMode
                  ? `${displayCount} pupil${displayCount === 1 ? '' : 's'}' families`
                  : `${displayCount} parent${displayCount === 1 ? '' : 's'}`}
              </span>{' '}
              <span className="text-gray-500">
                {teacherMode ? 'will be notified' : 'will receive this'} · {audienceLabel}
              </span>
              {teacherMode && (
                <div className="flex items-center gap-1.5 text-gray-400 text-xs mt-1">
                  <AlertCircle size={13} /> Exact parent recipients are resolved when you publish.
                </div>
              )}
              {!teacherMode && !directoryReady && (
                <div className="flex items-center gap-1.5 text-amber-600 text-xs mt-1">
                  <AlertCircle size={13} /> Staff directory still loading — publish is disabled
                  until parents are available to resolve.
                </div>
              )}
            </div>
            <AccentButton icon={Send} onClick={publish} disabled={!canPublish}>
              {teacherMode ? 'Send notice' : 'Publish notice'}
            </AccentButton>
          </div>
        </div>
      </SoftCard>

      {/* Recently sent */}
      {recent.length > 0 && (
        <SoftCard>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
            {teacherMode ? 'Your recent notices' : 'Recently sent'}
          </h3>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {recent.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => setTracking(n)}
                className="w-full py-3 flex items-start justify-between gap-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 -mx-2 px-2 rounded-lg transition-colors"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white truncate">
                      {n.title}
                    </span>
                    {n.priority === 'high' && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                        High
                      </span>
                    )}
                    {n.noticeType === 'homework' && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                        Homework
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {n.audienceLabel} · {n.recipientCount} parent
                    {n.recipientCount === 1 ? '' : 's'}
                    {' · '}
                    {new Date(n.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="shrink-0 text-xs flex items-center gap-3">
                  {n.emailedAt ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600">
                      <CheckCircle2 size={13} /> Emailed
                    </span>
                  ) : n.emailOnPublish ? (
                    <span className="inline-flex items-center gap-1 text-gray-400">
                      <Mail size={13} /> Queued
                    </span>
                  ) : null}
                  <span className="text-[#2d5295] font-medium">Receipts</span>
                </div>
              </button>
            ))}
          </div>
        </SoftCard>
      )}

      {tracking && <NoticeReadTracking notice={tracking} onClose={() => setTracking(null)} />}

      <SaveConfirmation
        show={save.show}
        status={save.status}
        message={save.message}
        onDismiss={() => setSave((s) => ({ ...s, show: false }))}
      />
    </div>
  );
}
